import { useState, useEffect, useMemo } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { updateItemStage, groupKanbanDataByStage } from '../../../utils/dataTransformers';
import axios from 'axios';
import api from '../../../api';

export const useKanban = (initialItems, searchQuery = '', refreshData) => {
  const [items, setItems] = useState(initialItems);
  const [stages, setStages] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    async function fetchStages() {
      try {
        const res = await api.get('/crm/lead-statuses');
        setStages(res.data.map(status => status.name));
      } catch (err) {
        console.error('Failed to fetch stages', err);
      }
    }
    fetchStages();
  }, []);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery || searchQuery.trim() === '') {
      return items;
    }

    const query = searchQuery.toLowerCase().trim();
    
    return items.filter(item => {
      // Search in multiple fields
      const searchableFields = [
        item.projectName || '',
        item.companyName || '',
        item.pic || '',
        item.email || '',
        item.description || '',
        item.sector || '',
        item.leadSource || '',
        item.stage || ''
      ];

      return searchableFields.some(field => 
        field.toLowerCase().includes(query)
      );
    });
  }, [items, searchQuery]);

  const handleDragStart = (event) => {
    // Prevent drag if already updating
    if (isUpdating) {
      event.preventDefault();
      return;
    }
    setActiveId(event.active.id);
    document.body.style.cursor = 'grabbing';
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over) {
      if (over.data?.current?.type === 'column') {
        setOverStage(over.data.current.stage);
      } else {
        const overItem = items.find(item => item.id === over.id);
        if (overItem) {
          setOverStage(overItem.stage);
        }
      }
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    document.body.style.cursor = '';

    console.log('🎯 Drag End Event:', {
      activeId: active.id,
      overId: over?.id,
      overType: over?.data?.current?.type,
      overStage: over?.data?.current?.stage,
      hasOver: !!over
    });

    if (!over) {
      console.log('❌ No drop target - drag cancelled');
      setActiveId(null);
      setOverStage(null);
      return;
    }

    // Get the current item being dragged
    const draggedItem = items.find(item => item.id === active.id);
    const currentStage = draggedItem?.stage;

    let targetStage = null;
    let shouldCallAPI = false;

    if (over.data?.current?.type === 'column') {
      // Dropped on a column
      targetStage = over.data.current.stage;
      shouldCallAPI = targetStage !== currentStage; // Only call API if stage actually changed
      console.log('📁 Dropped on column:', { targetStage, currentStage, shouldCallAPI });
    } else {
      // Dropped on another item - get the stage from that item
      const targetItem = items.find(item => item.id === over.id);
      if (targetItem) {
        targetStage = targetItem.stage;
        shouldCallAPI = targetStage !== currentStage; // Only call API if stage actually changed
        console.log('📋 Dropped on item:', { targetStage, currentStage, shouldCallAPI });
      }
    }

    // Always clear drag states first
    setActiveId(null);
    setOverStage(null);

    if (shouldCallAPI && targetStage) {
      console.log('🚀 Making API call to update stage');
      
      // Set updating state to prevent multiple operations
      setIsUpdating(true);
      
      // Optimistically update the UI immediately
      setItems(items => updateItemStage(items, active.id, targetStage));
      
      try {
        // Show loading state
        setIsRefreshing(true);
        
        // Make API call with timeout
        const apiCall = api.patch(`/crm/leads/${active.id}/stage`, {
          stage: targetStage
        });
        
        // Set a timeout to handle potential chunked encoding issues
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000); // 10 second timeout
        });
        
        const response = await Promise.race([apiCall, timeoutPromise]);
        
        console.log('✅ API call successful:', response.status);
        
        // Show success message
        setShowSuccess(true);
        setTimeout(() => setShowSuccess(false), 3000);
        
        // Refresh data from server to ensure consistency
        if (refreshData && typeof refreshData === 'function') {
          await refreshData();
        }
        
      } catch (error) {
        console.error('❌ API call failed:', error);
        
        // Check if this is a network encoding error but the request actually succeeded
        const isNetworkEncodingError = error.code === 'ERR_NETWORK' && 
          error.message === 'Network Error' && 
          error.config?.url?.includes('/stage');
        
        // Check if the error is related to chunked encoding but status was 200
        const isChunkedEncodingError = error.message?.includes('ERR_INCOMPLETE_CHUNKED_ENCODING') ||
          (error.code === 'ERR_NETWORK' && error.message === 'Network Error');
        
        // Check if this is a timeout error (which might indicate the request succeeded but response parsing failed)
        const isTimeoutError = error.message === 'Request timeout';
        
        if (isNetworkEncodingError || isChunkedEncodingError || isTimeoutError) {
          console.log('🔄 Network encoding error detected, but operation likely succeeded');
          
          // Don't revert the optimistic update for network encoding errors
          // Show success message instead
          setShowSuccess(true);
          setTimeout(() => setShowSuccess(false), 3000);
          
          // Still refresh data to ensure consistency
          if (refreshData && typeof refreshData === 'function') {
            try {
              await refreshData();
            } catch (refreshError) {
              console.warn('Failed to refresh data after network encoding error:', refreshError);
            }
          }
          
          // For network encoding errors, we'll assume success since the data is actually updating
          // The refreshData call above will sync the UI with the server state
        } else {
          // This is a real error, revert the optimistic update
          setItems(items => updateItemStage(items, active.id, currentStage || ''));
          
          // Show error message to user
          alert(`Failed to update stage: ${error.response?.data?.message || error.message}`);
        }
      } finally {
        // Always clear loading and updating states
        setIsRefreshing(false);
        setIsUpdating(false);
      }
    } else if (active.id !== over.id && over.data?.current?.type !== 'column') {
      // Just reordering within the same column - no API call needed
      console.log('🔄 Reordering within same column - no API call needed');
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const updatedItems = arrayMove(items, oldIndex, newIndex);
        return updatedItems;
      });
    } else {
      console.log('ℹ️ No action needed - same stage or invalid drop');
    }
  };

  const handleDragCancel = () => {
    document.body.style.cursor = '';
    setActiveId(null);
    setOverStage(null);
  };
  
  const kanbanData = groupKanbanDataByStage(filteredItems, stages);

  return {
    items: filteredItems,
    allItems: items, // Keep original items for reference
    stages,
    activeId,
    overStage,
    isRefreshing,
    showSuccess,
    isUpdating,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragCancel,
    kanbanData,
    searchQuery
  };
}; 